import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { AnnouncementComponent } from '@app/components/announcement/announcement.component';
import { AnnouncementService } from '@app/services/announcement.service';
import { AuthService } from '@app/services/auth.service';
import { FbPixelService } from '@shared/services/fb-pixel.service';
import { AnalyticsService } from 'libs/shared/src/lib/services/analytics.service';
import { filter } from 'rxjs';
import { HeaderComponent } from '../../components/header/header.component';
import { SharedService } from '../../services/shared.service';
import { UserService } from '../../services/user.service';
import { AdminNavBarComponent } from '../../components/nav-bar/nav-bar.component';

@Component({
  selector: 'blu-admin',
  templateUrl: './admin.component.html',
  styleUrls: ['./admin.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  standalone: true,
  imports: [
    AdminNavBarComponent,
    HeaderComponent,
    RouterOutlet,
    AnnouncementComponent,
  ],
})
export class AdminComponent {
  announcementService = inject(AnnouncementService);
  public sharedService = inject(SharedService);
  public userService = inject(UserService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  public analyticsService = inject(AnalyticsService);
  private authService = inject(AuthService);
  private fbPixelService = inject(FbPixelService);

  currentPath = signal<string>('');
  announcements = computed(() => {
    const path = this.currentPath();
    return this.announcementService.getAnnouncements(path);
  });

  menuDrawerVisible = false;
  constructor() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((route) => {
        const url = (route as NavigationEnd).url;
        this.currentPath.set(url);
      });

    effect(async () => {
      const params = this.route.snapshot.queryParamMap;
      const user = this.userService.user();
      const org = this.userService.organization();
      if (user === undefined && org === undefined) return;
      if ((user || org) && (!user || !org)) return;

      // handle stripe checkout redirect
      const event = params.get('event');
      const token = params.get('t');

      if (event) {
        const name = decodeURI(event);
        this.analyticsService.logEvent(name);
        if (name.includes('success')) {
          const value = getEventValue(name);
          this.fbPixelService.track('Purchase', { currency: 'BRL', value });
        }

        if (!token) {
          return this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { event: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
        }
      }

      if (token) {
        try {
          await this.authService.signInWithCustomToken(token);
        } catch {
          return this.router.navigateByUrl('/sign-in/admin');
        }
        return this.router.navigate([{ outlets: { modal: ['plans'] } }], {
          relativeTo: this.route,
          queryParams: { t: null, event: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }

      if (user?.type !== 'admin') {
        return this.router.navigateByUrl('/sign-in/admin');
      }

      const endDate = org?.subscription?.current_period_end?.toMillis();
      if (endDate && Date.now() < endDate) {
        return this.router.navigate([{ outlets: { modal: null } }], {
          relativeTo: this.route,
        });
      }
      return this.router.navigate([{ outlets: { modal: ['plans'] } }], {
        relativeTo: this.route,
      });
    });
  }
}

function getEventValue(name: string) {
  const lowerName = name.toLowerCase();
  if (lowerName.includes('individual')) {
    return 127;
  } else if (lowerName.includes('básico')) {
    return 297;
  } else if (lowerName.includes('pro')) {
    return 597;
  }
  return 0;
}
